"use client";

import { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countryCodes } from "./CountryCode";

export default function RegisterForm({ switchTab, onSuccess }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("register");
  const [userId, setUserId] = useState(null);
  const [otp, setOtp] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    countryCode: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // ================= INPUT HANDLERS =================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 15) setForm({ ...form, phone: value });
  };

  // ================= REGISTER =================
  const handleRegister = async (e) => {
    e.preventDefault();

    // ===== VALIDATIONS =====

    // Name
    if (!form.name.trim() || form.name.length < 2) {
      showErrorToast("Please enter valid name (min 2 characters)");
      return;
    }

    // Email
    const emailRegex =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(form.email)) {
      showErrorToast("Please enter valid email address");
      return;
    }

    // Country Code
    if (!form.countryCode) {
      showErrorToast("Please select country code");
      return;
    }

    // Phone
    if (!form.phone) {
      showErrorToast("Please enter valid 10 digit mobile number");
      return;
    }

    // Password match
    if (form.password !== form.confirmPassword) {
      showErrorToast("Passwords do not match");
      return;
    }

    // Strong Password - min 8 chars with number + special char
    const passwordRegex = /^(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/;

    if (!passwordRegex.test(form.password)) {
      showErrorToast(
        "Password must be at least 8 characters & include 1 number & 1 special character"
      );
      return;
    }

    // ===== API CALL =====
    setLoading(true);

    try {
      const response = await makeRequest("POST", "/user/register", {
        name: form.name,
        email: form.email,
        countryCodes: form.countryCode,
        mobile: form.phone,
        password: form.password,
      });

      if (response.error) {
        showErrorToast(response.message || "Register failed");
        return;
      }

      setUserId(response?.data?.userId);
      setStep("otp");
      showSuccessToast("OTP sent successfully");
    } catch (err) {
      showErrorToast(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ================= OTP VERIFY =================
  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp) {
      showErrorToast("Enter OTP");
      return;
    }

    setLoading(true);

    try {
      const response = await makeRequest("POST", "/user/register-verify", {
        userId,
        otp,
      });

      if (response.error) {
        showErrorToast(response.message || "OTP verification failed");
        return;
      }

      const { token, ...user } = response.data;
      const expiresAt = Date.now() + 30 * 60 * 1000;

      localStorage.setItem("token", token);
      localStorage.setItem("tokenExpiry", expiresAt);
      localStorage.setItem("user", JSON.stringify(user));

      showSuccessToast("Registered Successfully 🎉");
      onSuccess?.();
      navigate("/dashboard");
    } catch (err) {
      showErrorToast(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================
  return (
    <>
      <ToastContainer />

      {/* Loader */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#132b45]"></div>
            <p className="mt-4 text-[#132b45] font-medium">Loading...</p>
          </div>
        </div>
      )}

      {step === "otp" ? (
        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <h2 className="text-xl font-bold text-center text-[#162e40]">
            Verify OTP
          </h2>

          <input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, ""))
            }
            maxLength={6}
            className="w-full p-3 border rounded-md"
          />

          <button
            disabled={loading}
            className="w-full py-3 bg-[#162e40] text-white rounded-md"
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>
      ) : (
        <form className="space-y-3" onSubmit={handleRegister}>
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
            className="w-full p-3 border rounded-md"
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            className="w-full p-3 border rounded-md"
            required
          />

          {/* Phone */}
          <div className="flex flex-wrap gap-3">
            <select
              value={form.countryCode}
              onChange={(e) =>
                setForm({ ...form, countryCode: e.target.value })
              }
              className="w-40 p-3 border rounded-md bg-white w-full md:w-auto cursor-pointer"
            >
              {countryCodes.map((item, idx) => (
                <option key={idx} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>

            <input
              type="tel"
              placeholder="Phone Number"
              value={form.phone}
              onChange={handlePhoneChange}
              maxLength={15}
              className="flex-1 p-3 border rounded-md"
              required
            />
          </div>

          {/* Password */}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              className="w-full p-3 border rounded-md"
              required
            />
            <span
              className="absolute right-4 top-4 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </span>
          </div>

          {/* Confirm */}
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password"
              value={form.confirmPassword}
              onChange={handleChange}
              className="w-full p-3 border rounded-md"
              required
            />
            <span
              className="absolute right-4 top-4 cursor-pointer"
              onClick={() =>
                setShowConfirmPassword(!showConfirmPassword)
              }
            >
              {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
            </span>
          </div>

          <button
            disabled={loading}
            className="w-full py-3 bg-[#162e40] text-white rounded-md hover:bg-[#0f2332] transition"
          >
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>
      )}
    </>
  );
}
